import React from "react";
import { motion } from "framer-motion";
import PicImg from "../PicImg";

/**
 * Why Choose LIFTILE — split layout with real product photograph on left
 * and 6 (or N) trust cards on right.
 *
 * Props:
 *  - testId
 *  - image: string (public path)
 *  - imageAlt: string
 *  - imageCaptionEyebrow?: string (default "In Operation")
 *  - imageCaption: string
 *  - floatingBadge?: { top: string, bottom: string }  (e.g., { top: "Up to", bottom: "150 metres" })
 *  - eyebrow (default "Why LIFTILE")
 *  - heading (ReactNode)
 *  - subtitle
 *  - trustCards: { Icon, title, desc }[]
 *  - background?: "cream" | "cream-light"  (default "cream-light")
 */
export default function WhyChooseLiftile({
  testId,
  image,
  imageAlt,
  imageCaptionEyebrow = "In Operation",
  imageCaption,
  floatingBadge,
  eyebrow = "Why LIFTILE",
  heading,
  subtitle,
  trustCards = [],
  background = "cream-light",
}) {
  const bgClass =
    background === "cream" ? "bg-liftile-cream" : "bg-liftile-cream-light";

  return (
    <section
      data-testid={testId}
      className={`relative ${bgClass} py-24 md:py-32 text-liftile-matte-black overflow-hidden`}
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 md:gap-14 lg:gap-16 items-start">
          {/* LEFT — Real project photograph */}
          <motion.div
            initial={{ opacity: 0, x: -22, scale: 0.98 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.35)]">
              <PicImg
                src={image}
                alt={imageAlt}
                className="w-full h-full object-cover" loading="lazy" decoding="async" />
              <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-liftile-matte-black/85 via-liftile-matte-black/40 to-transparent" />
              <div className="absolute bottom-7 left-7 right-7">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-px bg-liftile-orange" />
                  <span className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-orange font-semibold">
                    {imageCaptionEyebrow}
                  </span>
                </div>
                <p className="font-heading text-base md:text-lg text-liftile-cream font-medium leading-snug">
                  {imageCaption}
                </p>
              </div>
            </div>

            {floatingBadge && (
              <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-liftile-orange text-white px-5 py-3 rounded-xl shadow-[0_18px_40px_-12px_rgba(255,107,26,0.55)]">
                <div className="font-heading text-[10px] uppercase tracking-[0.22em] font-semibold">
                  {floatingBadge.top}
                </div>
                <div className="font-display text-lg mt-0.5">
                  {floatingBadge.bottom}
                </div>
              </div>
            )}
          </motion.div>

          {/* RIGHT — Trust cards */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65 }}
              className="mb-10 md:mb-12"
            >
              <div className="heading-eyebrow mb-3">{eyebrow}</div>
              <h2 className="display-2">{heading}</h2>
              {subtitle && (
                <p className="font-body text-base md:text-lg text-liftile-matte-black/70 mt-5 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {trustCards.map((t, i) => (
                <motion.div
                  key={t.title}
                  data-testid={`${testId}-card-${i}`}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="group relative h-full bg-white rounded-2xl p-6 shadow-[0_3px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_15px_35px_-12px_rgba(255,107,26,0.22)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-0.5 transition-all duration-400"
                >
                  <div className="w-11 h-11 rounded-xl bg-liftile-orange/10 group-hover:bg-liftile-orange flex items-center justify-center mb-4 transition-colors duration-400">
                    <t.Icon
                      className="w-5 h-5 text-liftile-orange group-hover:text-white transition-colors duration-400"
                      strokeWidth={1.6}
                    />
                  </div>
                  <h3 className="font-heading text-sm uppercase tracking-wider font-semibold text-liftile-matte-black leading-snug">
                    {t.title}
                  </h3>
                  <p className="font-body text-[13.5px] text-liftile-matte-black/68 mt-2 leading-relaxed">
                    {t.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
